import * as React from 'react';
import './Navbar.scss';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';

import { Button } from '../FormComponents/Buttons/Button';
import Logo from '../../images/logo-flexterity.svg';
import SettingsIcon from '../../images/icon-settings.svg';
import { useCookieConsent } from '../../hooks/useCookieConsent';
import { CookiePreferencesModal } from '../Modals/CookiePreferencesModal/CookiePreferencesModal';

interface NavbarProps {
  gaCode: string;
}

const Navbar = ({ gaCode }: NavbarProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout, isAuthenticated } = useAuth0();
  const { analyticsEnabled, handleAccept, handleDecline } = useCookieConsent(gaCode);
  const [isCookieSettingsModalOpen, setIsCookieSettingsModalOpen] = React.useState(false);
  const [isAnalyticsToggleOn, setIsAnalyticsToggleOn] = React.useState(analyticsEnabled);

  const loadHomePage = () => {
    navigate('/home');
  };

  // Seed the toggle from the current cookie state here, at the point the modal is opened,
  // rather than resyncing it with an effect inside the (always-mounted) modal.
  const openCookieSettings = () => {
    setIsAnalyticsToggleOn(analyticsEnabled);
    setIsCookieSettingsModalOpen(true);
  };

  const handleSaveCookieSettings = () => {
    if (isAnalyticsToggleOn) {
      handleAccept();
    } else {
      handleDecline();
    }
    setIsCookieSettingsModalOpen(false);
  };

  const handleEnableAllCookies = () => {
    setIsAnalyticsToggleOn(true);
    handleAccept();
    setIsCookieSettingsModalOpen(false);
  };

  return (
    <>
      <nav className="navbar" role="navigation" aria-label="main navigation">
        <div className="navbar-menu">
          <div className="navbar-start" onClick={loadHomePage}>
            <img src={Logo} alt="Flexterity" />
          </div>

          <div className="navbar-end">
            {isAuthenticated && (
              <>
                <NavLink
                  to="/home"
                  className={({ isActive }) => (isActive ? 'navbar-item is-active' : 'navbar-item')}
                >
                  {t('navbar.clients')}
                </NavLink>
                <NavLink
                  to="/exerciseslist"
                  className={({ isActive }) => (isActive ? 'navbar-item is-active' : 'navbar-item')}
                >
                  {t('navbar.exercises')}
                </NavLink>
                <NavLink
                  to="/my-details"
                  className={({ isActive }) => (isActive ? 'navbar-item is-active' : 'navbar-item')}
                >
                  {t('navbar.my-details')}
                </NavLink>
                <NavLink
                  to="/help"
                  className={({ isActive }) => (isActive ? 'navbar-item is-active' : 'navbar-item')}
                >
                  {t('navbar.help')}
                </NavLink>
              </>
            )}
            {isAuthenticated && (
              <button
                type="button"
                className="navbar-cookie-settings"
                title={t('navbar.cookie-settings')}
                aria-label={t('navbar.cookie-settings')}
                onClick={openCookieSettings}
              >
                <img src={SettingsIcon} alt="" />
              </button>
            )}
            {isAuthenticated && (
              <Button
                translationKey="navbar.logout"
                type="button"
                variant="outlined"
                colour="black"
                onClick={() => logout({ returnTo: window.location.origin })}
                additionalClassName="is-small"
              />
            )}
          </div>
        </div>
      </nav>
      <CookiePreferencesModal
        isOpen={isCookieSettingsModalOpen}
        handleCloseModal={() => setIsCookieSettingsModalOpen(false)}
        isAnalyticsOn={isAnalyticsToggleOn}
        onToggleAnalytics={setIsAnalyticsToggleOn}
        onEnableAll={handleEnableAllCookies}
        onSave={handleSaveCookieSettings}
      />
    </>
  );
};

export default Navbar;
